import React, { useState, useSyncExternalStore } from "react";
// Material UI imports
import { Card, Slide, alpha, useTheme } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
// Helper functions
import { getIsAppUpdateReady, subscribeToAppUpdate } from "utils/appUpdate";

/**
 * UpdatePrompt tells the user a new version of the app is ready when the automatic reload was
 * held back because a screen had unsaved work, and lets them reload once they are done.
 * @returns {JSX.Element} The rendered update prompt, hidden until an update is waiting
 * @example
 * <UpdatePrompt />
 */
function UpdatePrompt() {
  const theme = useTheme();
  const isUpdateReady = useSyncExternalStore(subscribeToAppUpdate, getIsAppUpdateReady);
  const [isDismissed, setIsDismissed] = useState(false);
  const isOpen = isUpdateReady && !isDismissed;

  return (
    <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
      <MDBox
        sx={{
          position: "fixed",
          bottom: { xs: 16, md: 24 },
          left: { xs: 16, md: "50%" },
          transform: { xs: "none", md: "translateX(-50%)" },
          zIndex: theme.zIndex.snackbar,
          width: { xs: "calc(100% - 32px)", sm: "auto" },
          maxWidth: { xs: "none", sm: 480 },
        }}
      >
        <Card
          sx={{
            p: 2.5,
            backgroundColor: theme.palette.background.card,
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.info.main, 0.25)}`,
          }}
        >
          <MDTypography variant="h6" fontWeight="medium" color="info">
            A new version is available
          </MDTypography>
          <MDTypography variant="body2" color="text" mt={0.5}>
            Finish and submit your current work, then reload. Reloading now discards anything not
            yet submitted.
          </MDTypography>
          <MDBox display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
            <MDButton
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => setIsDismissed(true)}
              sx={{
                border: `1px solid ${theme.palette.transparent.main}`,
                "&:hover": {
                  color: theme.palette.error.main,
                  borderColor: theme.palette.error.main,
                },
              }}
            >
              Later
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              onClick={() => window.location.reload()}
            >
              Reload
            </MDButton>
          </MDBox>
        </Card>
      </MDBox>
    </Slide>
  );
}

export default UpdatePrompt;
